import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { useInfiniteQuery, useQuery } from '@tanstack/react-query';
import { ClipboardList, Search, Calendar, Download } from 'lucide-react';
import { vendorApi } from '../../services/api';
import Sidebar from '../../components/Sidebar';
import TopBar from '../../components/TopBar';
import StatusBadge from '../../components/StatusBadge';
import { formatCurrency, formatDate, isBlankDate } from '../../utils/format';
import { exportCsv } from '../../utils/exportCsv';

const PAGE_SIZE = 500;

type View = 'open' | 'held' | 'settled';

const views: { id: View; label: string; description: string }[] = [
  { id: 'open', label: 'Kommande avräkning', description: 'Godkända transaktioner som tas med i nästa avräkning' },
  { id: 'held', label: 'Vilande', description: 'Hålls inne tills förseningen eller kreditrisken är löst' },
  { id: 'settled', label: 'Avräknade', description: 'Transaktioner som finns på en bokförd avräkning' },
];

/** Where one transaction is on its way to payment */
export function transactionStatus(order: any): { tone: 'turquoise' | 'brass' | 'red' | 'green' | 'gray'; label: string } {
  if (order.status === 'Order') {
    if (order.creditRisk === 'Yes') return { tone: 'red', label: 'Kreditrisk' };
    if (order.orderLate === 'Yes') return { tone: 'brass', label: 'Försenad' };
    return { tone: 'turquoise', label: 'Nästa avräkning' };
  }
  if (order.settlementDocumentPaid === true) return { tone: 'green', label: 'Utbetald' };
  if (order.settlementDocumentPaid === false) return { tone: 'gray', label: 'Avräknad, ej utbetald' };
  return { tone: 'gray', label: 'Avräknad' };
}

export default function SettlementOrders() {
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const view: View = (['open', 'held', 'settled'] as View[]).includes(searchParams.get('view') as View)
    ? (searchParams.get('view') as View)
    : 'open';
  const [searchTerm, setSearchTerm] = useState('');
  const [monthFilter, setMonthFilter] = useState('');

  const { data: vendorData, error } = useQuery({
    queryKey: ['vendor', 'me'],
    queryFn: async () => (await vendorApi.me()).data,
  });

  useEffect(() => {
    if (error) navigate('/login');
  }, [error, navigate]);

  const {
    data,
    isLoading,
    isError,
    fetchNextPage,
    hasNextPage,
    isFetchingNextPage,
  } = useInfiniteQuery({
    queryKey: ['vendor', 'settlement-orders', view],
    initialPageParam: 0,
    queryFn: async ({ pageParam }) =>
      (await vendorApi.getSettlementOrders({ view, $top: PAGE_SIZE, $skip: pageParam })).data,
    getNextPageParam: (lastPage, pages) =>
      (lastPage?.orders?.length || 0) < PAGE_SIZE ? undefined : pages.length * PAGE_SIZE,
  });

  const allOrders: any[] = useMemo(() => data?.pages.flatMap((p) => p.orders || []) || [], [data]);

  const filteredOrders = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    return allOrders.filter((order) => {
      const matchesSearch =
        !term ||
        [order.no, order.orderId, order.customerNo, order.name, order.customerReference, order.resourceNo, order.externalDocumentNo, order.internalDocumentNo]
          .some((v) => String(v || '').toLowerCase().includes(term));
      const matchesMonth = !monthFilter || order.orderDate?.startsWith(monthFilter);
      return matchesSearch && matchesMonth;
    });
  }, [allOrders, searchTerm, monthFilter]);

  const months = useMemo(
    () => Array.from(new Set(allOrders.map((o) => o.orderDate?.substring(0, 7)).filter(Boolean))).sort().reverse() as string[],
    [allOrders]
  );

  const totalAmount = filteredOrders.reduce((sum, o) => sum + (o.settlementOrderAmount || 0), 0);
  const current = views.find((v) => v.id === view)!;

  const selectView = (id: View) => {
    setMonthFilter('');
    setSearchParams(id === 'open' ? {} : { view: id });
  };

  const exportRows = () =>
    exportCsv(`transaktioner-${view}-${new Date().toISOString().slice(0, 10)}`, [
      { header: 'Orderdatum', value: (o: any) => o.orderDate },
      { header: 'Ordernr', value: (o: any) => o.no },
      { header: 'Order-ID', value: (o: any) => o.orderId },
      { header: 'Kundnr', value: (o: any) => o.customerNo },
      { header: 'Kund', value: (o: any) => o.name },
      { header: 'Kundreferens', value: (o: any) => o.customerReference },
      { header: 'Resurs', value: (o: any) => o.resourceNo },
      { header: 'Externt dokumentnr', value: (o: any) => o.externalDocumentNo },
      { header: 'Från', value: (o: any) => [o.shipFromName, o.shipFromCity].filter(Boolean).join(', ') },
      { header: 'Till', value: (o: any) => [o.shipToName, o.shipToCity].filter(Boolean).join(', ') },
      { header: 'Status', value: (o: any) => transactionStatus(o).label },
      { header: 'Orsak', value: (o: any) => o.reasonDeniedOrLate },
      { header: 'Avräkning', value: (o: any) => (o.status === 'Order' ? '' : o.internalDocumentNo) },
      { header: 'Avräkningsdatum', value: (o: any) => o.settlementPostingDate },
      { header: 'Utbetald', value: (o: any) => o.settlementDocumentPaidDate },
      { header: 'Belopp', value: (o: any) => o.settlementOrderAmount || 0 },
      { header: 'Provision', value: (o: any) => o.commissionAmount || 0 },
    ], filteredOrders);

  return (
    <div className="flex min-h-screen bg-gray-50">
      <Sidebar logoUrl={vendorData?.tenant?.logoUrl} companyName={vendorData?.tenant?.companyName} />

      <div className="flex flex-1 flex-col">
        <TopBar userName={vendorData?.vendor?.fullName || vendorData?.vendor?.username} />

        <main className="mx-auto w-full max-w-[1400px] flex-1 px-6 py-8 lg:px-10 lg:py-10">
          <div className="mb-6 flex flex-wrap items-end justify-between gap-4">
            <div>
              <h1 className="font-display text-[1.35rem] leading-snug text-ink text-balance md:text-[1.6rem]">Transaktioner</h1>
              <p className="mt-1 text-gray-600">{current.description}</p>
            </div>
            <button
              type="button"
              onClick={exportRows}
              disabled={filteredOrders.length === 0}
              className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2 text-sm font-medium text-ink ring-1 ring-inset ring-gray-200 transition-all duration-300 ease-fluid hover:ring-blue-300 active:scale-[0.98] disabled:opacity-50"
            >
              <Download className="h-4 w-4" aria-hidden />
              Exportera till Excel
            </button>
          </div>

          {/* Views */}
          <div role="tablist" aria-label="Status" className="mb-6 inline-flex flex-wrap gap-1 rounded-full bg-white p-1 ring-1 ring-inset ring-gray-200">
            {views.map((v) => (
              <button
                key={v.id}
                role="tab"
                type="button"
                aria-selected={view === v.id}
                onClick={() => selectView(v.id)}
                className={`rounded-full px-4 py-1.5 text-sm font-medium transition-all duration-300 ease-fluid ${
                  view === v.id ? 'bg-navy text-white' : 'text-gray-600 hover:text-ink'
                }`}
              >
                {v.label}
              </button>
            ))}
          </div>

          {/* Filters */}
          <div className="card mb-6 p-4">
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden />
                <input
                  type="search"
                  aria-label="Sök"
                  placeholder="Sök ordernr, kund, resurs, referens, faktura…"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="w-full rounded-full border border-gray-300 py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-primary"
                />
              </div>
              <div className="relative">
                <Calendar className="absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden />
                <select
                  aria-label="Månad"
                  value={monthFilter}
                  onChange={(e) => setMonthFilter(e.target.value)}
                  className="w-full rounded-full border border-gray-300 py-2 pl-10 pr-4 focus:border-transparent focus:ring-2 focus:ring-primary"
                >
                  <option value="">Alla månader</option>
                  {months.map((m) => (
                    <option key={m} value={m}>
                      {new Date(m + '-01').toLocaleDateString('sv-SE', { month: 'long', year: 'numeric' })}
                    </option>
                  ))}
                </select>
              </div>
            </div>
            <p className="mt-4 flex flex-wrap justify-between gap-2 text-sm text-gray-600">
              <span>
                Visar {filteredOrders.length} av {allOrders.length} transaktioner{hasNextPage && ' (fler finns)'}
              </span>
              <span className="tabular-nums">
                Summa <strong className="font-semibold text-ink">{formatCurrency(totalAmount)}</strong>
              </span>
            </p>
          </div>

          <div className="card">
            {isLoading ? (
              <div className="p-12 text-center text-gray-500">Laddar transaktioner…</div>
            ) : isError ? (
              <p role="alert" className="p-6 text-sm text-red-800">Transaktionerna kunde inte hämtas. Försök igen om en stund.</p>
            ) : filteredOrders.length > 0 ? (
              <div className="overflow-x-auto">
                <table className="min-w-full divide-y divide-gray-200">
                  <thead className="bg-gray-50">
                    <tr>
                      {['Orderdatum', 'Ordernr', 'Kund', 'Resurs', 'Referens', 'Status'].map((h) => (
                        <th key={h} scope="col" className="px-5 py-3 text-left text-xs font-medium uppercase tracking-wider text-gray-500">
                          {h}
                        </th>
                      ))}
                      <th scope="col" className="px-5 py-3 text-right text-xs font-medium uppercase tracking-wider text-gray-500">
                        Belopp
                      </th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100 bg-white">
                    {filteredOrders.map((order) => {
                      const status = transactionStatus(order);
                      return (
                        <tr
                          key={order.systemId}
                          onClick={() => navigate(`/settlement-orders/${order.systemId}`)}
                          className="cursor-pointer transition-colors hover:bg-blue-50/60"
                        >
                          <td className="whitespace-nowrap px-5 py-3.5 text-sm text-gray-900">{formatDate(order.orderDate)}</td>
                          <td className="whitespace-nowrap px-5 py-3.5">
                            <Link
                              to={`/settlement-orders/${order.systemId}`}
                              onClick={(e) => e.stopPropagation()}
                              className="font-mono text-sm font-medium text-ink hover:text-blue-700"
                            >
                              {order.no}
                            </Link>
                          </td>
                          <td className="px-5 py-3.5 text-sm">
                            <span className="block text-gray-900">{order.name || order.customerNo}</span>
                            {order.name && <span className="block text-xs text-gray-500">{order.customerNo}</span>}
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5 font-mono text-sm text-gray-700">{order.resourceNo || '–'}</td>
                          <td className="px-5 py-3.5 text-sm text-gray-600">{order.customerReference || order.externalDocumentNo || '–'}</td>
                          <td className="px-5 py-3.5 text-sm">
                            <StatusBadge tone={status.tone}>{status.label}</StatusBadge>
                            {view === 'held' && order.reasonDeniedOrLate && (
                              <span className="mt-1 block max-w-[32ch] text-xs text-gray-600">{order.reasonDeniedOrLate}</span>
                            )}
                            {view === 'settled' && order.internalDocumentNo && (
                              <span className="mt-1 block text-xs text-gray-600">
                                {order.settlementDocumentType ? (
                                  <Link
                                    to={`/invoices/${order.settlementDocumentType}/${encodeURIComponent(order.internalDocumentNo)}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="font-mono text-blue-700 hover:underline"
                                  >
                                    {order.internalDocumentNo}
                                  </Link>
                                ) : (
                                  <span className="font-mono">{order.internalDocumentNo}</span>
                                )}
                                {!isBlankDate(order.settlementDocumentPaidDate) && ` · ${formatDate(order.settlementDocumentPaidDate)}`}
                              </span>
                            )}
                          </td>
                          <td className="whitespace-nowrap px-5 py-3.5 text-right text-sm font-medium tabular-nums text-gray-900">
                            {formatCurrency(order.settlementOrderAmount)}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="p-12 text-center">
                <ClipboardList className="mx-auto mb-4 h-12 w-12 text-gray-400" aria-hidden />
                <h2 className="mb-2 text-base font-semibold text-ink">Inga transaktioner</h2>
                <p className="text-gray-600">
                  {searchTerm || monthFilter
                    ? 'Inget matchar sökningen. Prova att ändra filtren.'
                    : view === 'held'
                      ? 'Inga transaktioner hålls inne just nu.'
                      : view === 'open'
                        ? 'Det finns inga transaktioner som väntar på avräkning.'
                        : 'Inga avräknade transaktioner ännu.'}
                </p>
              </div>
            )}
          </div>

          {hasNextPage && (
            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => fetchNextPage()}
                disabled={isFetchingNextPage}
                className="rounded-full px-5 py-2 text-sm font-medium text-ink ring-1 ring-inset ring-gray-200 transition-all duration-300 ease-fluid hover:bg-white hover:ring-blue-300 active:scale-[0.98] disabled:opacity-50"
              >
                {isFetchingNextPage ? 'Laddar…' : `Ladda fler (${allOrders.length} laddade)`}
              </button>
            </div>
          )}
        </main>
      </div>
    </div>
  );
}
